import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useShipToApi } from "./useShipToApi";
import { useIsSessionCurrent, useSessionSetAtom } from "../useSessionSetAtom";
import { useWallReRead } from "../useWallReRead";
import { createReadGuard } from "./_helpers/readGuard";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { sessionEpochAtom } from "@/store/sessionAtoms";
import { asApiFailure, markReported } from "@utils/apiFailure";
import {
    cartWriteRefusal,
    type ShoppingCartResponse,
    type AddToCartRequest,
    type ShoppingCartItem,
} from "@/models/shoppingCart";
import {
    shoppingCartAtom,
    isProcessingShoppingCartAtom,
} from "@/store/shoppingCartAtoms";

const SHOPPING_CART_ENDPOINT = "/shoppingcarts";

// Which read is allowed to publish to the cart atoms. Same shape and same reason as
// useProductApi's: the atoms are module-scope while every caller's guard is per-mount, and
// switching the ship-to location supersedes an in-flight cart read (#228 / E4-S12). Letting the
// previous destination's answer land would put the OLD isRestricted flags back on the lines
// under a header that already names the new location.
//
// The publish watermark (`claimPublish`): a successful read publishes when nothing NEWER has
// already published — so an older read's good cart is not discarded because the read that
// superseded it went on to fail. A cart write supersedes every read begun so far (below). The
// pages keep each read's outcome themselves (#407), so there are no status atoms to guard.
const cartReads = createReadGuard();

// The cart read in flight, shared by every caller asking for the same cart meanwhile (PR 356
// review): CustomerContainer reads it for the header badge, and the Cart, Checkout and the
// vendor switch read it on purpose, often in the same commit — one request, one toast. Keyed on
// the session and the destination: another destination is another answer, and a read from an
// ended session publishes nothing in the new one. The FIRST caller's options govern the shared
// request (a page joining the badge's quiet read still shows its own failed state).
let inFlightCartRead: {
    key: string;
    promise: Promise<ShoppingCartResponse | null>;
} | null = null;

// A cart write (PR 356 review, finding 1): every read started before it is superseded. It
// cannot publish over the write — an Add to Cart landing while the badge's mount read is out
// would otherwise lose its units when the pre-add answer lands — and is not joined by a read
// asked for afterwards, which must see the write. Called as a write starts and as it settles:
// a read started during the write may have been answered before the server applied it.
const supersedeCartReads = () => {
    cartReads.supersede();
    inFlightCartRead = null;
};

// #368: every cart write's 403 is held from useApi's toast, and runCartWrite answers it
// instead (below).
const suppressForbidden = (status: number | undefined): boolean => status === 403;

// The toast useApi raised for a cart write's 403 before #368: handleGenericError's title,
// and the body every 403 carries.
const FORBIDDEN_TOAST = {
    title: "Error 403",
    message: "You don't have access to this.",
};

// Add to cart, the quantity update and the vendor switch answer a coded 400 when the line
// can't be had as asked (#443 / C-07): insufficient_stock, product_unavailable,
// offering_unavailable or line_quantity_limit (cartWriteRefusal). Their callers show it inline
// (Shopping/_helpers/stockCopy.ts), so useApi's toast stays quiet for exactly those —
// recognised by code and fields, never wording. A 403 is held too (suppressForbidden).
// Every other failure, a validation failure included, keeps useApi's own handling. A caller
// of any of the three MUST render the coded refusal itself.
const suppressCartWriteRefusal = (
    status: number | undefined,
    _errorCode: string | undefined,
    _hasApiBody: boolean,
    bodyText: string,
): boolean =>
    suppressForbidden(status) ||
    (status === 400 && cartWriteRefusal(bodyText) !== undefined);

export const useShoppingCartApi = () => {
    const { callApi } = useApi();
    const { callShipToApi, shipToLocationId } = useShipToApi();
    const sessionEpoch = useAtomValue(sessionEpochAtom);
    // Session-bound: an answer landing after sign-out must not refill the
    // cart endSessionAtom cleared.
    const shoppingCart = useAtomValue(shoppingCartAtom);
    const setShoppingCart = useSessionSetAtom(shoppingCartAtom);
    const setIsProcessing = useSetAtom(isProcessingShoppingCartAtom);
    const reReadWallsCustomer = useWallReRead();
    const isSessionCurrent = useIsSessionCurrent();
    const { showError } = useNotification();

    // `skipErrorNotification` is for a caller with nothing to show a failure on: the header
    // badge's read (PR 356 review, finding 3). Pages that show the cart keep the toast.
    const getShoppingCart = useCallback(
        (
            options: { skipErrorNotification?: boolean } = {},
        ): Promise<ShoppingCartResponse | null> => {
            const key = `${sessionEpoch}|${shipToLocationId ?? ""}`;
            if (inFlightCartRead?.key === key) {
                return inFlightCartRead.promise;
            }

            const { claimPublish } = cartReads.begin();

            const read = (async () => {
                const cart = await callShipToApi<ShoppingCartResponse>(
                    SHOPPING_CART_ENDPOINT,
                    options,
                );

                if (claimPublish()) {
                    setShoppingCart(cart);
                }

                return cart;
            })();
            inFlightCartRead = { key, promise: read };
            // Settled: the next caller asks again (unless a write already let it go).
            const release = () => {
                if (inFlightCartRead?.promise === read) inFlightCartRead = null;
            };
            read.then(release, release);

            return read;
        },
        [
            callShipToApi,
            shipToLocationId,
            sessionEpoch,
            setShoppingCart,
        ],
    );

    // Every cart write runs through here: the processing flag, and the reads it supersedes as
    // it starts and as it settles (finding 1). A FAILED write has published nothing, yet the
    // reads in flight were let go too, so the cart is read again — quietly, the write's own
    // failure is already said — and the badge ends on the server's figure rather than a read
    // behind (Lead, Q6). After the settling supersede, which would otherwise cut it off.
    //
    // A 403 (#368) carries the API's one generic sentence, so /users/me is read again first,
    // as Checkout does for Place Order: a wall that went up since the page loaded (an
    // organization put on hold, say) refuses cart writes too. When the fresh answer walls the
    // customer, ApprovalGate moves them to the wall, which says why, and nothing is toasted
    // over it. Otherwise — another refusal, or a re-read that failed — the toast useApi held is
    // raised here and the failure marked reported, unless the session ended meanwhile (useApi's
    // own rule). The failure is rethrown either way, so every caller handles it as before.
    const runCartWrite = useCallback(
        async (write: () => Promise<void>): Promise<void> => {
            setIsProcessing(true);
            supersedeCartReads();
            let failed = true;

            try {
                await write();
                failed = false;
            } catch (error) {
                if (
                    asApiFailure(error)?.status === 403 &&
                    !(await reReadWallsCustomer()) &&
                    isSessionCurrent()
                ) {
                    showError(FORBIDDEN_TOAST);
                    markReported(error);
                }
                throw error;
            } finally {
                supersedeCartReads();
                setIsProcessing(false);
                if (failed) {
                    getShoppingCart({ skipErrorNotification: true }).catch(() => {
                        /* the badge keeps what it had; the pages read on their own */
                    });
                }
            }
        },
        [
            setIsProcessing,
            getShoppingCart,
            reReadWallsCustomer,
            isSessionCurrent,
            showError,
        ],
    );

    const addToCart = useCallback(
        (request: AddToCartRequest): Promise<void> =>
            runCartWrite(async () => {
                await callApi(SHOPPING_CART_ENDPOINT, {
                    method: "POST",
                    body: JSON.stringify(request),
                    skipErrorNotification: suppressCartWriteRefusal,
                });

                const newItem: ShoppingCartItem = {
                    id: "",
                    productId: request.productId,
                    name: request.productId,
                    price: 0,
                    // Placeholder line: vendor id/name are unknown here (the
                    // add-to-cart request carries only vendorSkuId); the next
                    // getShoppingCart() replaces this with the real payload.
                    vendorId: 0,
                    vendorName: "",
                    vendorSkuId: request.vendorSkuId,
                    isActive: true,
                    // A restricted product has no offers, so nothing can reach add-to-cart for
                    // one; the next getShoppingCart replaces this line with the server's flag
                    // either way.
                    isRestricted: false,
                    quantity: request.quantity,
                };

                if (shoppingCart && shoppingCart.items) {
                    // Merge by the offering (vendorSkuId), not the product:
                    // under multi-vendor (§3.11) the same product from two
                    // vendors is two distinct lines (Bug #302).
                    const existingItem = shoppingCart.items.find(
                        (item) => item.vendorSkuId === request.vendorSkuId,
                    );

                    if (existingItem) {
                        const updatedItems = shoppingCart.items.map((item) =>
                            item.vendorSkuId === request.vendorSkuId
                                ? {
                                    ...item,
                                    quantity:
                                          item.quantity + request.quantity,
                                }
                                : item,
                        );
                        setShoppingCart({
                            ...shoppingCart,
                            items: updatedItems,
                        });
                    } else {
                        setShoppingCart({
                            ...shoppingCart,
                            items: [...shoppingCart.items, newItem],
                        });
                    }
                } else {
                    setShoppingCart({
                        id: "",
                        userId: "",
                        items: [newItem],
                    });
                }
            }),
        [callApi, runCartWrite, shoppingCart, setShoppingCart],
    );

    // The two per-item mutations below deliberately leave `shoppingCartAtom`
    // alone: the caller owns the optimistic write and the revert-on-rejection,
    // because only it knows which line (vendorSkuId) the edit targeted and what
    // the pre-edit value was. `callApi` already raises the error toast — except
    // for the quantity update's coded refusals, which the caller shows on the
    // line (#443), and a 403, which runCartWrite answers (#368).
    // Both guard against a blank id: `addToCart` above inserts an optimistic
    // placeholder line with `id: ""` that stays interactive until the next
    // getShoppingCart replaces it, and `/shoppingcarts/items/` with no id is a
    // malformed request.
    const updateCartItemQuantity = useCallback(
        async (itemId: string, quantity: number): Promise<void> => {
            if (!itemId) return;

            await runCartWrite(async () => {
                await callApi(`${SHOPPING_CART_ENDPOINT}/items/${itemId}`, {
                    method: "PATCH",
                    body: JSON.stringify({ quantity }),
                    skipErrorNotification: suppressCartWriteRefusal,
                });
            });
        },
        [callApi, runCartWrite],
    );

    const removeCartItem = useCallback(
        async (itemId: string): Promise<void> => {
            if (!itemId) return;

            await runCartWrite(async () => {
                await callApi(`${SHOPPING_CART_ENDPOINT}/items/${itemId}`, {
                    method: "DELETE",
                    skipErrorNotification: suppressForbidden,
                });
            });
        },
        [callApi, runCartWrite],
    );

    // Moves a line to another vendor offering of the same sku (§3.11 per-line vendor pick;
    // #443 / C-07 offers it when the line's vendor is short). The API merges the line into
    // an existing one for that offering, so the caller re-reads the cart rather than
    // patching the atom; the id guard is the same as the two methods above.
    const switchCartItemVendor = useCallback(
        async (itemId: string, vendorSkuId: string): Promise<void> => {
            if (!itemId) return;

            await runCartWrite(async () => {
                await callApi(`${SHOPPING_CART_ENDPOINT}/${itemId}`, {
                    method: "PUT",
                    body: JSON.stringify({ vendorSkuId }),
                    skipErrorNotification: suppressCartWriteRefusal,
                });
            });
        },
        [callApi, runCartWrite],
    );

    const clearShoppingCart = useCallback(
        (): Promise<void> =>
            runCartWrite(async () => {
                await callApi(SHOPPING_CART_ENDPOINT, {
                    method: "DELETE",
                    skipErrorNotification: suppressForbidden,
                });

                if (shoppingCart) {
                    setShoppingCart({
                        ...shoppingCart,
                        items: [],
                    });
                }
            }),
        [callApi, runCartWrite, shoppingCart, setShoppingCart],
    );

    return {
        getShoppingCart,
        addToCart,
        updateCartItemQuantity,
        removeCartItem,
        switchCartItemVendor,
        clearShoppingCart,
    };
};
